import { ariaShortcut, formatShortcut } from '@lyrmo/shared/shortcuts';
import {
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { useShortcuts } from '../shortcuts/useShortcuts';
import { HIGHLIGHT_NAMES, TEXT_COLOR_NAMES, type Highlight, type TextColor } from './colors';
import { FORMAT_KEYS } from './format-keys';
import {
  linkTarget,
  type Block,
  type Format,
  type Formats,
  type Paint,
  type TableAction,
} from './markdown-edit';
import { moveWithArrows, usePopover } from './usePopover';
import './format-bar.css';

interface FormatBarProps {
  /** The draft it formats, for screen readers. */
  readonly fieldId?: string | undefined;
  readonly formats: Formats;
  readonly disabled: boolean;
  readonly onMark: (kind: Format) => void;
  readonly onBlock: (block: Block) => void;
  /** Links the selection, or with null, unlinks it. */
  readonly onLink: (href: string | null) => void;
  readonly onClear: () => void;
  /** Puts a divider in, after the line the caret is in. */
  readonly onDivider: () => void;
  /** Colors the selection, or highlights it; with null, takes the color or highlight away. */
  readonly onPaint: (kind: Paint, value: TextColor | Highlight | null) => void;
  /** Puts a table in, or changes the one the caret is in. */
  readonly onTable: (action: TableAction | 'insert') => void;
  /** Back to the draft, as when a menu closes. */
  readonly onReturn: () => void;
  /** Opens the link editor, as ⌘K does, each time it changes. */
  readonly linkRequest: number;
}

const STYLES: readonly Extract<
  Block,
  'paragraph' | 'heading1' | 'heading2' | 'heading3' | 'heading4' | 'heading5' | 'heading6'
>[] = ['paragraph', 'heading1', 'heading2', 'heading3', 'heading4', 'heading5', 'heading6'];

// The marks the More formatting menu has, as Jira's does, each with its icon.
const MORE: readonly (readonly [Exclude<Format, 'strong' | 'em'>, IconName])[] = [
  ['underline', 'underline'],
  ['strike', 'strike'],
  ['code', 'code'],
  ['sub', 'subscript'],
  ['sup', 'superscript'],
];

// The bar's stops, in order, for the one Tab lands on.
const ORDER = [
  'style',
  'strong',
  'em',
  'more',
  'color',
  'bullet',
  'ordered',
  'task',
  'link',
  'codeBlock',
  'quote',
  'divider',
  'table',
] as const;
type Stop = (typeof ORDER)[number];

/** Keeps the draft's focus, and its selection, when a button is clicked. */
const keepFocus = (event: MouseEvent) => {
  event.preventDefault();
};

/**
 * The formatting toolbar over the draft, laid out as Jira's: the line's style, then bold and
 * italics with the rest of what words can be in a menu after them, and their color, then lists,
 * then a link, a code block, a quote, a divider, and a table. It's one stop for Tab, and the arrow keys move along it;
 * its buttons leave the focus, and the selection, in the draft.
 */
export function FormatBar({
  fieldId,
  formats,
  disabled,
  onMark,
  onBlock,
  onLink,
  onClear,
  onDivider,
  onPaint,
  onTable,
  onReturn,
  linkRequest,
}: FormatBarProps) {
  const { t } = useI18n();
  const { platform } = useShortcuts();
  const rowRef = useRef<HTMLDivElement>(null);
  const [stop, setStop] = useState(0);

  // One stop for Tab; arrows, Home, and End move along the bar, round and round.
  function rove(event: KeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key) || event.target instanceof HTMLInputElement) return;
    if ((event.target as HTMLElement).closest('.menu-panel')) return;
    const stops = [
      ...(rowRef.current?.querySelectorAll<HTMLElement>('.format-stop:not(:disabled)') ?? []),
    ];
    if (stops.length === 0) return;
    event.preventDefault();
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const at = Math.max(0, stops.indexOf(document.activeElement as HTMLElement));
    const forward = event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? stops.length - 1
          : (at + (forward ? 1 : -1) + stops.length) % stops.length;
    stops[next]?.focus();
  }

  /** What a stop needs to be the one Tab lands on. */
  const roving = (name: Stop) => ({
    tabIndex: stop === ORDER.indexOf(name) ? 0 : -1,
    onFocus: () => {
      setStop(ORDER.indexOf(name));
    },
  });

  const button = (
    name: Stop,
    icon: IconName,
    label: string,
    pressed: boolean | undefined,
    onClick: () => void,
    keys: string | null = null,
  ) => (
    <button
      type="button"
      className="format-button format-stop"
      aria-label={label}
      aria-pressed={pressed}
      aria-keyshortcuts={keys ? ariaShortcut(keys, platform) : undefined}
      title={keys ? `${label} (${formatShortcut(keys, platform)})` : label}
      disabled={disabled}
      data-format={name}
      onMouseDown={keepFocus}
      onClick={onClick}
      {...roving(name)}
    >
      <Icon name={icon} />
    </button>
  );
  const mark = (kind: 'strong' | 'em', icon: IconName, label: string) =>
    button(
      kind,
      icon,
      label,
      formats.marks.has(kind),
      () => {
        onMark(kind);
      },
      FORMAT_KEYS[kind],
    );
  const block = (
    name: Extract<Stop, 'bullet' | 'ordered' | 'task' | 'codeBlock' | 'quote'>,
    kind: Extract<Block, 'bullet' | 'ordered' | 'task' | 'code' | 'quote'>,
    icon: IconName,
    label: string,
  ) =>
    button(
      name,
      icon,
      label,
      formats.block === kind,
      () => {
        onBlock(kind);
      },
      kind === 'bullet' || kind === 'ordered' || kind === 'task' ? FORMAT_KEYS[kind] : null,
    );

  return (
    <div
      className="format-bar"
      role="toolbar"
      aria-label={t.format.toolbar}
      aria-controls={fieldId}
      onKeyDown={rove}
    >
      <div
        ref={rowRef}
        className="format-row"
        onMouseDown={(event) => {
          // A click between the buttons keeps the draft's focus too.
          if (event.target === event.currentTarget) event.preventDefault();
        }}
      >
        <StyleMenu
          value={formats.block}
          disabled={disabled}
          onChange={onBlock}
          onReturn={onReturn}
          {...roving('style')}
        />
        <Divider />
        {mark('strong', 'bold', t.format.bold)}
        {mark('em', 'italic', t.format.italic)}
        <MoreMenu
          marks={formats.marks}
          disabled={disabled}
          onMark={onMark}
          onClear={onClear}
          onReturn={onReturn}
          {...roving('more')}
        />
        <ColorMenu
          color={formats.color}
          highlight={formats.highlight}
          disabled={disabled}
          onPaint={onPaint}
          onReturn={onReturn}
          {...roving('color')}
        />
        <Divider />
        {block('bullet', 'bullet', 'bullets', t.format.bullets)}
        {block('ordered', 'ordered', 'numbered', t.format.numbered)}
        {block('task', 'task', 'checklist', t.format.checklist)}
        <Divider />
        <LinkEditor
          link={formats.link}
          disabled={disabled}
          request={linkRequest}
          onLink={onLink}
          onReturn={onReturn}
          {...roving('link')}
        />
        {block('codeBlock', 'code', 'code-block', t.format.styles.code)}
        {block('quote', 'quote', 'quote', t.format.styles.quote)}
        {button('divider', 'divider', t.format.divider, undefined, onDivider)}
        <TableMenu
          inTable={formats.table}
          disabled={disabled}
          onTable={onTable}
          onReturn={onReturn}
          {...roving('table')}
        />
      </div>
    </div>
  );
}

function Divider() {
  return <span className="format-divider" aria-hidden="true" />;
}

/** Where a menu hangs: under its button, measured from the bar's start. */
function menuStart(trigger: HTMLElement): number {
  const button = trigger.getBoundingClientRect();
  const bar = trigger.closest('.format-bar')?.getBoundingClientRect();
  if (!bar) return 0;
  const rtl = getComputedStyle(trigger).direction === 'rtl';
  return rtl ? bar.right - button.right : button.left - bar.left;
}

/**
 * A menu off the bar: opened with the mouse, it leaves the focus, and the selection, in the
 * draft; from the keyboard, its row in force, or its first, takes the focus, for the arrow keys.
 */
function useBarMenu(disabled: boolean) {
  const popover = usePopover(!disabled);
  const panelRef = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState(0);
  const panelId = useId();
  const fromKeyboard = useRef(false);
  const { shown, setOpen } = popover;

  useEffect(() => {
    if (!shown || !fromKeyboard.current) return;
    const rows = panelRef.current?.querySelectorAll<HTMLElement>('.menu-row');
    (panelRef.current?.querySelector<HTMLElement>('[aria-current="true"]') ?? rows?.[0])?.focus();
  }, [shown]);

  return {
    ...popover,
    panelRef,
    panelId,
    /** What the menu's button does with the mouse and the keyboard. */
    trigger: {
      'aria-haspopup': 'dialog',
      'aria-expanded': shown,
      'aria-controls': panelId,
      onMouseDown: (event: MouseEvent<HTMLButtonElement>) => {
        fromKeyboard.current = false;
        event.preventDefault();
      },
      onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown') return;
        fromKeyboard.current = true;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setStart(menuStart(event.currentTarget));
          setOpen(true);
        }
      },
      onClick: (event: MouseEvent<HTMLButtonElement>) => {
        if (!shown) setStart(menuStart(event.currentTarget));
        setOpen((open) => !open);
      },
    } as const,
    /** Where the panel hangs. */
    panelStyle: { '--menu-start': `${String(start)}px` } as CSSProperties,
  };
}

interface StopProps {
  readonly tabIndex: number;
  readonly onFocus: () => void;
}

interface StyleMenuProps extends StopProps {
  readonly value: Block | null;
  readonly disabled: boolean;
  readonly onChange: (block: Block) => void;
  readonly onReturn: () => void;
}

/** What the line is, Normal text or a heading, and the menu to change it. */
function StyleMenu({ value, disabled, tabIndex, onFocus, onChange, onReturn }: StyleMenuProps) {
  const { t } = useI18n();
  const {
    present,
    leaving,
    setOpen,
    rootRef,
    triggerRef,
    onBlur,
    panelRef,
    panelId,
    trigger,
    panelStyle,
  } = useBarMenu(disabled);
  const current = STYLES.find((style) => style === value) ?? (value ? null : 'paragraph');
  const name = current ? t.format.styles[current] : t.format.textStyle;

  return (
    <div className="format-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className="format-style format-stop"
        aria-label={`${t.format.textStyle}: ${name}`}
        title={t.format.textStyle}
        tabIndex={tabIndex}
        disabled={disabled}
        onFocus={onFocus}
        {...trigger}
      >
        <span className="format-style-icon" aria-hidden="true">
          <Icon name="heading" />
        </span>
        <span key={current ?? ''} className="format-style-name" aria-hidden="true">
          {name}
        </span>
        <span className="option-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>
      {present && (
        <div
          ref={panelRef}
          className={cx('menu-panel format-panel', leaving && 'is-leaving')}
          style={panelStyle}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={t.format.textStyle}
          onKeyDown={moveWithArrows}
        >
          <ul className="menu-list format-styles">
            {STYLES.map((style) => (
              <li key={style}>
                <button
                  type="button"
                  className={cx('menu-row menu-stop format-style-row', `is-${style}`)}
                  aria-current={style === current ? 'true' : undefined}
                  onMouseDown={keepFocus}
                  onClick={() => {
                    setOpen(false);
                    onChange(style);
                    onReturn();
                  }}
                >
                  <span className="format-style-label">{t.format.styles[style]}</span>
                  {style !== 'paragraph' && (
                    // What to type in the draft for it.
                    <kbd className="format-menu-key" aria-hidden="true">
                      {'#'.repeat(Number(style.slice(-1)))}
                    </kbd>
                  )}
                  {style === current && (
                    <span className="menu-row-tick" aria-hidden="true">
                      <Icon name="check" />
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface MoreMenuProps extends StopProps {
  readonly marks: ReadonlySet<Format>;
  readonly disabled: boolean;
  readonly onMark: (kind: Format) => void;
  readonly onClear: () => void;
  readonly onReturn: () => void;
}

/**
 * More formatting, as Jira's ··· has it: underline, strikethrough, code, subscript, and
 * superscript, each shown on where the selection has it, and clearing formatting. Its button is
 * on while any of them is.
 */
function MoreMenu({
  marks,
  disabled,
  tabIndex,
  onFocus,
  onMark,
  onClear,
  onReturn,
}: MoreMenuProps) {
  const { t } = useI18n();
  const { platform } = useShortcuts();
  const {
    present,
    leaving,
    setOpen,
    rootRef,
    triggerRef,
    onBlur,
    panelRef,
    panelId,
    trigger,
    panelStyle,
  } = useBarMenu(disabled);
  const label = t.format.more;
  const names: Readonly<Record<(typeof MORE)[number][0], string>> = {
    underline: t.format.underline,
    strike: t.format.strike,
    code: t.format.code,
    sub: t.format.subscript,
    sup: t.format.superscript,
  };
  const row = (
    key: string,
    icon: IconName,
    name: string,
    on: boolean | undefined,
    keys: string | null,
    onClick: () => void,
  ) => (
    <li key={key}>
      <button
        type="button"
        className={cx('menu-row menu-stop format-more-row', on && 'is-on')}
        aria-pressed={on}
        aria-keyshortcuts={keys ? ariaShortcut(keys, platform) : undefined}
        onMouseDown={keepFocus}
        onClick={() => {
          setOpen(false);
          onClick();
          onReturn();
        }}
      >
        <span className="format-more-icon" aria-hidden="true">
          <Icon name={icon} />
        </span>
        <span className="format-style-label">{name}</span>
        {keys && (
          <kbd className="format-menu-key" aria-hidden="true">
            {formatShortcut(keys, platform)}
          </kbd>
        )}
      </button>
    </li>
  );

  return (
    <div className="format-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={cx(
          'format-button format-stop',
          MORE.some(([kind]) => marks.has(kind)) && 'is-active',
        )}
        aria-label={label}
        title={label}
        tabIndex={tabIndex}
        disabled={disabled}
        data-format="more"
        onFocus={onFocus}
        {...trigger}
      >
        <Icon name="more" />
      </button>
      {present && (
        <div
          ref={panelRef}
          className={cx('menu-panel format-panel format-more-panel', leaving && 'is-leaving')}
          style={panelStyle}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={label}
          onKeyDown={moveWithArrows}
        >
          <ul className="menu-list">
            {MORE.map(([kind, icon]) =>
              row(
                kind,
                icon,
                names[kind],
                marks.has(kind),
                kind === 'sub' || kind === 'sup' ? null : FORMAT_KEYS[kind],
                () => {
                  onMark(kind);
                },
              ),
            )}
          </ul>
          <ul className="menu-list format-more-end">
            {row('clear', 'clear-format', t.format.clear, undefined, FORMAT_KEYS.clear, onClear)}
          </ul>
        </div>
      )}
    </div>
  );
}

interface ColorMenuProps extends StopProps {
  readonly color: TextColor | null;
  readonly highlight: Highlight | null;
  readonly disabled: boolean;
  readonly onPaint: (kind: Paint, value: TextColor | Highlight | null) => void;
  readonly onReturn: () => void;
}

/**
 * The words' color, as Jira's text color menu has it: an A underlined in the color they're in,
 * and a palette of colors and highlights, each with a swatch to take it off again.
 */
function ColorMenu({
  color,
  highlight,
  disabled,
  tabIndex,
  onFocus,
  onPaint,
  onReturn,
}: ColorMenuProps) {
  const { t } = useI18n();
  const {
    present,
    leaving,
    setOpen,
    rootRef,
    triggerRef,
    onBlur,
    panelRef,
    panelId,
    trigger,
    panelStyle,
  } = useBarMenu(disabled);
  const label = t.format.color;
  const swatch = (
    kind: Paint,
    value: TextColor | Highlight | null,
    name: string,
    current: string | null,
  ) => (
    <button
      key={`${kind}-${value ?? 'none'}`}
      type="button"
      className={cx('menu-stop format-swatch', `is-${kind}`, `is-${value ?? 'none'}`)}
      aria-label={name}
      aria-pressed={value === current}
      title={name}
      onMouseDown={keepFocus}
      onClick={() => {
        setOpen(false);
        onPaint(kind, value);
        onReturn();
      }}
    >
      {kind === 'color' ? 'A' : null}
    </button>
  );

  return (
    <div className="format-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={cx(
          'format-button format-stop',
          (color !== null || highlight !== null) && 'is-active',
        )}
        aria-label={label}
        title={label}
        tabIndex={tabIndex}
        disabled={disabled}
        data-format="color"
        data-color={color ?? undefined}
        onFocus={onFocus}
        {...trigger}
      >
        <Icon name="text-color" />
        <span className="format-color-bar" aria-hidden="true" />
      </button>
      {present && (
        <div
          ref={panelRef}
          className={cx('menu-panel format-panel format-color-panel', leaving && 'is-leaving')}
          style={panelStyle}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={label}
          onKeyDown={moveAcross}
        >
          <p className="format-color-title" aria-hidden="true">
            {label}
          </p>
          <div className="format-swatches" role="group" aria-label={label}>
            {swatch('color', null, t.format.colors.default, color)}
            {TEXT_COLOR_NAMES.map((name) => swatch('color', name, t.format.colors[name], color))}
          </div>
          <p className="format-color-title" aria-hidden="true">
            {t.format.highlight}
          </p>
          <div className="format-swatches" role="group" aria-label={t.format.highlight}>
            {swatch('highlight', null, t.format.noHighlight, highlight)}
            {HIGHLIGHT_NAMES.map((name) =>
              swatch('highlight', name, t.format.colors[name], highlight),
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** The arrow keys along a palette's swatches, row after row, as reading goes. */
function moveAcross(event: KeyboardEvent<HTMLElement>) {
  const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
  if (!keys.includes(event.key)) return;
  const stops = [...event.currentTarget.querySelectorAll<HTMLElement>('.menu-stop')];
  const at = stops.indexOf(document.activeElement as HTMLElement);
  const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
  const forward = event.key === 'ArrowDown' || event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
  event.preventDefault();
  stops[(at + (forward ? 1 : -1) + stops.length) % stops.length]?.focus();
}

interface TableMenuProps extends StopProps {
  /** Whether the caret is in a table, for the menu to change it, rather than putting one in. */
  readonly inTable: boolean;
  readonly disabled: boolean;
  readonly onTable: (action: TableAction | 'insert') => void;
  readonly onReturn: () => void;
}

const TABLE_ACTIONS: readonly (readonly [TableAction, IconName])[] = [
  ['addRow', 'plus'],
  ['addColumn', 'plus'],
  ['deleteRow', 'close'],
  ['deleteColumn', 'close'],
  ['deleteTable', 'trash'],
];

/**
 * The table button: it puts a table in, or in one, opens what to do to it: add a row or a column
 * after the caret's, or take its row, its column, or the table away.
 */
function TableMenu({ inTable, disabled, tabIndex, onFocus, onTable, onReturn }: TableMenuProps) {
  const { t } = useI18n();
  const {
    present,
    leaving,
    setOpen,
    rootRef,
    triggerRef,
    onBlur,
    panelRef,
    panelId,
    trigger,
    panelStyle,
  } = useBarMenu(disabled);
  const label = t.format.table;

  return (
    <div className="format-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={cx('format-button format-stop', inTable && 'is-active')}
        aria-label={label}
        title={label}
        tabIndex={tabIndex}
        disabled={disabled}
        data-format="table"
        onFocus={onFocus}
        {...(inTable
          ? trigger
          : {
              onMouseDown: keepFocus,
              onClick: () => {
                onTable('insert');
              },
            })}
      >
        <Icon name="table" />
      </button>
      {present && (
        <div
          ref={panelRef}
          className={cx('menu-panel format-panel format-more-panel', leaving && 'is-leaving')}
          style={panelStyle}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={label}
          onKeyDown={moveWithArrows}
        >
          <ul className="menu-list">
            {TABLE_ACTIONS.map(([action, icon]) => (
              <li key={action} className={cx(action === 'deleteRow' && 'format-more-end')}>
                <button
                  type="button"
                  className="menu-row menu-stop format-more-row"
                  onMouseDown={keepFocus}
                  onClick={() => {
                    setOpen(false);
                    onTable(action);
                    onReturn();
                  }}
                >
                  <span className="format-more-icon" aria-hidden="true">
                    <Icon name={icon} />
                  </span>
                  <span className="format-style-label">{t.format.tableActions[action]}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface LinkEditorProps extends StopProps {
  /** Where the link the selection is in goes, to change it or take it away. */
  readonly link: string | null;
  readonly disabled: boolean;
  readonly request: number;
  readonly onLink: (href: string | null) => void;
  readonly onReturn: () => void;
}

/** The link button, and the field it opens for where the link goes. */
function LinkEditor({
  link,
  disabled,
  request,
  tabIndex,
  onFocus,
  onLink,
  onReturn,
}: LinkEditorProps) {
  const { t } = useI18n();
  const { platform } = useShortcuts();
  const { shown, present, leaving, setOpen, rootRef, triggerRef, onBlur } = usePopover(!disabled);
  const inputRef = useRef<HTMLInputElement>(null);
  const [address, setAddress] = useState('');
  const [problem, setProblem] = useState(false);
  const [start, setStart] = useState(0);
  const panelId = useId();
  const label = t.format.link;

  const open = () => {
    if (disabled) return;
    setAddress(link ?? '');
    setProblem(false);
    if (triggerRef.current) setStart(menuStart(triggerRef.current));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    onReturn();
  };
  /** Links the selection to the address typed, or says why it can't. */
  const apply = () => {
    const href = linkTarget(address);
    if (!href) {
      setProblem(true);
      inputRef.current?.focus();
      return;
    }
    onLink(href);
    close();
  };

  // ⌘K in the draft opens it, as the button does, with the link as it is then.
  const asked = useRef(request);
  const openAsked = useEffectEvent(open);
  useEffect(() => {
    if (request === asked.current) return;
    asked.current = request;
    openAsked();
  }, [request]);
  useEffect(() => {
    if (!shown) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [shown]);

  // Not a form of its own: it's in the editor's, which would take its submit for Rewrite.
  let body: ReactNode = null;
  if (present) {
    body = (
      <div
        className={cx('menu-panel format-panel format-link-panel', leaving && 'is-leaving')}
        style={{ '--menu-start': `${String(start)}px` } as CSSProperties}
        id={panelId}
        inert={leaving}
        role="dialog"
        aria-label={label}
      >
        <label className="format-link-field">
          <span className="sr-only">{t.format.linkAddress}</span>
          <Icon name="link" />
          <input
            ref={inputRef}
            className="menu-search-input"
            type="text"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            placeholder={t.format.linkPlaceholder}
            aria-invalid={problem || undefined}
            aria-describedby={problem ? `${panelId}-problem` : undefined}
            value={address}
            onChange={(event) => {
              setAddress(event.target.value);
              setProblem(false);
            }}
            onKeyDown={(event) => {
              // Enter applies the link, and stops there: in the editor's form, it would rewrite.
              if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
              event.preventDefault();
              apply();
            }}
          />
        </label>
        {problem && (
          <p className="format-link-problem" id={`${panelId}-problem`} role="alert">
            {t.format.badLink}
          </p>
        )}
        <div className="format-link-actions">
          {link && (
            <button
              type="button"
              className="menu-action"
              onClick={() => {
                onLink(null);
                close();
              }}
            >
              <Icon name="unlink" />
              {t.format.removeLink}
            </button>
          )}
          <button type="button" className="menu-action menu-action--accent" onClick={apply}>
            <Icon name="check" />
            {t.format.applyLink}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="format-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className="format-button format-stop"
        aria-label={label}
        aria-pressed={link !== null}
        aria-haspopup="dialog"
        aria-expanded={shown}
        aria-controls={panelId}
        aria-keyshortcuts={ariaShortcut(FORMAT_KEYS.link, platform)}
        title={`${label} (${formatShortcut(FORMAT_KEYS.link, platform)})`}
        tabIndex={tabIndex}
        disabled={disabled}
        data-format="link"
        onMouseDown={keepFocus}
        onFocus={onFocus}
        onClick={() => {
          if (shown) close();
          else open();
        }}
      >
        <Icon name="link" />
      </button>
      {body}
    </div>
  );
}
